import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { useApp, API } from "../context";
import { isPromptFree } from "../lib/promptFree";

/**
 * LaunchBanner, a slim, attractive, DISMISSIBLE scarcity banner shown above
 * the header. Admin-controlled via /api/admin/launch-mode/banner (on/off + copy
 * + founder-premium goal). Two states, auto-selected:
 *   • PUBLIC (logged out)  → "Become a Founder" (create a free account)
 *   • FOUNDER (logged in, not yet paid) → "Upgrade to Founder Premium" at the
 *     discounted founder rate; shows how many of the goal remain.
 * Hides entirely when disabled, when already Founder Premium, or once dismissed.
 */
export default function PrelaunchLaunchBanner() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchCfg = () => axios.get(`${API}/launch-mode`).then(r => setCfg(r.data)).catch(() => setCfg(null));
    fetchCfg();
    // Live tick-down: refresh the spots-remaining count periodically during launch.
    const iv = setInterval(fetchCfg, 60000);
    return () => clearInterval(iv);
  }, []);

  if (!cfg || !cfg.banner || !cfg.banner.enabled) return null;

  const isPremium = (user?.access_tier || 0) >= 3 || user?.tier_name === "Founder Premium" || user?.founder_tier === "founder_premium" || user?.founder_tier === "founder_vip";
  if (isPremium) return null; // nothing to upsell
  // Free-premium launch window: signed-in users already have everything free,
  // so don't show the founder/upgrade banner to them (public visitors still
  // see the "create a free account" variant).
  if (user && isPromptFree()) return null;

  const b = cfg.banner;
  const variant = user ? "founder" : "public";
  const dismissKey = `lb_dismissed_${variant}`;
  if (dismissed || (typeof window !== "undefined" && sessionStorage.getItem(dismissKey))) return null;

  const heading = variant === "founder" ? b.founder_heading : b.public_heading;
  const text = variant === "founder" ? b.founder_text : b.public_text;
  const cta = variant === "founder" ? b.founder_cta : b.public_cta;
  const dest = variant === "founder" ? "/subscribe" : "/signup";
  // During the "launch" phase show the live founder-cohort spots remaining;
  // otherwise fall back to the Founder-Premium goal remaining.
  const remaining = (cfg.mode === "launch" && typeof cfg.founders_remaining === "number")
    ? cfg.founders_remaining
    : cfg.founder_premium_remaining;

  const dismiss = () => {
    try { sessionStorage.setItem(dismissKey, "1"); } catch (_) { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="relative w-full bg-[linear-gradient(90deg,#7c3aed_0%,#db2777_50%,#f59e0b_100%)] text-white shadow-md"
      data-testid="launch-banner"
      role="region"
      aria-label="Founder offer"
    >
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-center gap-x-3 gap-y-1 flex-wrap text-center">
        <Sparkles size={15} className="shrink-0 text-white/90" />
        <span className="text-[13px] sm:text-sm font-fredoka font-bold tracking-tight">{heading}</span>
        <span className="hidden sm:inline text-white/85 text-xs">{text}</span>
        {typeof remaining === "number" && remaining > 0 && (
          <span className={`text-[11px] font-bold bg-black/25 rounded-full px-2 py-0.5 tabular-nums ${remaining <= 25 ? "animate-pulse ring-1 ring-white/60" : ""}`} data-testid="launch-banner-remaining">
            {remaining <= 25 ? "🔥 " : ""}Only {remaining} founder spots left
          </span>
        )}
        <button
          onClick={() => navigate(dest)}
          className="ml-1 rounded-full bg-white text-slate-900 text-xs font-bold px-3 py-1.5 flex items-center gap-1 hover:bg-white/90 transition-colors shadow-sm"
          data-testid="launch-banner-cta"
        >
          {cta} <ArrowRight size={12} />
        </button>
      </div>
      <button
        onClick={dismiss}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-white/70 hover:text-white hover:bg-white/15 transition-colors"
        aria-label="Dismiss"
        data-testid="launch-banner-dismiss"
      >
        <X size={15} />
      </button>
    </div>
  );
}
